import { useEffect, useState } from 'react';

//MUI
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

//common
import SchemaForm from 'common/SchemaForm';
import { STATUS_OPTIONS } from 'common/constants';
import { JobTitle, jobTitles, organizationTypes, orgIds } from 'enum/combobox';
import { removeEmptyKeys } from 'common/utils';

//screen
import type { AccountFilterModel } from 'modules/account/types';
import useGetOrganizationsList, {
  type OrganizationInfo,
} from 'modules/department/services/useGetOrganizationsList';

export interface SearchParams {
  pageSize: number;
  pageIndex: number;
  filters: AccountFilterModel;
}

interface FilterProps {
  filters: AccountFilterModel;
  onSetFilters: (filters: AccountFilterModel) => void;
  onClearFilters: () => void;
}

const FilterBox = ({ filters, onSetFilters, onClearFilters }: FilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [jobTitlesByOrgType, setJobTitlesByOrgType] = useState<JobTitle[]>([]);
  const [filteredOrg, setFilteredOrg] = useState<OrganizationInfo[]>([]);

  const { data: organizations } = useGetOrganizationsList();

  // Keep dependent option lists in sync with current orgType and organizations
  useEffect(() => {
    const ot = filters.orgType || '';
    filterJobTitles(ot);
    filterOrg(ot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.orgType, organizations]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handlePopoverClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const filtersCount = Object.keys(removeEmptyKeys(filters)).length;
  const orgTypes = [orgIds.system, orgIds.client, orgIds.govAgency];

  const filterJobTitles = (orgType: string) => {
    setJobTitlesByOrgType(() => {
      if (!orgType) return [];
      if (!orgTypes.includes(orgType)) return jobTitles;
      return jobTitles.filter((title) => title.organizationTypeId === orgType);
    });
  };

  // orgType
  const filterOrg = (orgType: string) => {
    setFilteredOrg(() => {
      if (!orgType || !organizations) return [];

      if (!orgTypes.includes(orgType)) {
        return organizations.map((org: OrganizationInfo) => ({
          ...org,
          id: org.operationCode,
        }));
      }

      return organizations.reduce((acc, nextOrg) => {
        if (nextOrg.orgType !== orgType) return acc;
        return [...acc, { ...nextOrg, id: nextOrg.operationCode }];
      }, [] as OrganizationInfo[]);
    });
  };

  return (
    <>
      <SchemaForm<AccountFilterModel>
        formData={filters}
        onSubmit={onSetFilters}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
              isMobile,
            },
          }) => {
            return {
              searchLabel: {
                mode: 'node',
                hidden: isMobile,
                render: <Typography sx={{ mt: 1 }}>{formatMessage({ id: 'search' })}:</Typography>,
                propsWrapper: { mobile: undefined },
              },
              name: {
                mode: 'text-field',
                placeholder: formatMessage({ id: 'account.search' }),
                propsWrapper: { mobile: true },
              },
              submit: {
                mode: 'button',
                type: 'submit',
                variant: 'contained',
                startIcon: <SearchRounded />,
                children: !isMobile && formatMessage({ id: 'search' }),
                propsWrapper: { mobile: undefined },
              },
              filter: {
                mode: 'button',
                variant: 'outlined',
                children: !isMobile && formatMessage({ id: 'filter' }),
                startIcon: (
                  <Badge badgeContent={filtersCount} variant="standard" color="primary">
                    <TuneOutlined />
                  </Badge>
                ),
                endIcon: !isMobile && <ExpandMoreOutlined />,
                onClick: handleClick,
                propsWrapper: { mobile: undefined },
              },
              reset: {
                mode: 'button',
                // hidden: isMobile,
                variant: 'outlined',
                children: <RefreshRounded />,
                onClick: onClearFilters,
                propsWrapper: { mobile: undefined },
              },
            };
          },
        }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        slotProps={{
          paper: {
            variant: 'outlined',
            elevation: 0,
            sx: {
              borderColor: 'primary.main',
              p: 2,
              mt: 2,
              width: 280,
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <SchemaForm<AccountFilterModel>
          key={`popover-filter-${filters.orgType ?? ''}-${filters.orgId ?? ''}-${filters.title ?? ''}-${filters.inactive ?? ''}`}
          formData={filters}
          schema={{
            fields: ({
              formProps: {
                intl: { formatMessage },
              },
            }) => {
              return {
                orgType: {
                  mode: 'select',
                  label: formatMessage({ id: 'account.orgType' }),
                  rawOptions: true,
                  options: [{ id: '', name: formatMessage({ id: 'all' }) }, ...organizationTypes],
                  disableClearBtn: true,
                  onChange: (orgTypeId: string) => {
                    filterJobTitles(orgTypeId);
                    filterOrg(orgTypeId);
                    // Reset dependent fields to avoid stale selections across org types
                    onSetFilters({
                      ...filters,
                      orgType: orgTypeId || '',
                      orgId: '',
                      title: '',
                    });
                  },
                },
                orgId: {
                  mode: 'select',
                  label: formatMessage({ id: 'account.unit' }),
                  rawOptions: true,
                  options: filteredOrg,
                  hasAllOptions: true,
                  disableClearBtn: true,
                  onChange: (orgId: string) => {
                    onSetFilters({
                      ...filters,
                      orgId: orgId || '',
                    });
                  },
                },
                title: {
                  mode: 'select',
                  label: formatMessage({ id: 'account.jobTitle' }),
                  rawOptions: true,
                  options: jobTitlesByOrgType,
                  hasAllOptions: true,
                  disableClearBtn: true,
                  onChange: (titleId: string) => {
                    onSetFilters({
                      ...filters,
                      title: titleId || '',
                    });
                  },
                },
                inactive: {
                  mode: 'select',
                  label: formatMessage({ id: 'statusAdmin' }),
                  options: STATUS_OPTIONS,
                  disableClearBtn: true,
                  hasAllOptions: true,
                  onChange: (val: boolean | '') => {
                    onSetFilters({
                      ...filters,
                      inactive: val === '' ? null : Boolean(val),
                    });
                  },
                },
              };
            },
          }}
        />
      </Popover>
    </>
  );
};
export default FilterBox;
